import React, { useState } from 'react';
import { Icon } from '@iconify/react';
const genId = () => `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const EMPTY_RULE = { id: '', label: '', startDate: '', endDate: '', price: '' };

export default function PricingRulesEditor({ rules = [], onChange, currencySymbol = '₦' }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [error, setError] = useState('');

  const openAdd = () => {
    setDraft({ ...EMPTY_RULE, id: genId() });
    setEditingIndex('new');
    setError('');
  };

  const openEdit = (idx) => {
    setDraft({ ...rules[idx] });
    setEditingIndex(idx);
    setError('');
  };

  const handleSave = () => {
    if (!draft.label.trim()) { setError('Label is required'); return; }
    if (!draft.startDate) { setError('Start date is required'); return; }
    if (!draft.endDate) { setError('End date is required'); return; }
    if (!draft.price || isNaN(parseFloat(draft.price)) || parseFloat(draft.price) <= 0) {
      setError('A valid price is required');
      return;
    }
    if (new Date(draft.startDate) >= new Date(draft.endDate)) {
      setError('End date must be after start date');
      return;
    }

    const newRules = [...rules];
    if (editingIndex === 'new') {
      newRules.push({ ...draft, price: parseFloat(draft.price) });
    } else {
      newRules[editingIndex] = { ...draft, price: parseFloat(draft.price) };
    }
    onChange(newRules);
    setEditingIndex(null);
    setDraft(EMPTY_RULE);
    setError('');
  };

  const handleDelete = (idx) => {
    const newRules = rules.filter((_, i) => i !== idx);
    onChange(newRules);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setDraft(EMPTY_RULE);
    setError('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Set custom nightly rates for specific date ranges (e.g. holidays, peak season). The rule that covers the most nights of a booking will be applied.
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="px-4 py-2 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary hover:text-primary-foreground transition-all flex items-center gap-2 whitespace-nowrap"
        >
          <Icon icon="lucide:plus" />
          Add Rule
        </button>
      </div>

      {/* Existing rules */}
      {rules.length > 0 && (
        <div className="space-y-2">
          {rules.map((rule, idx) => (
            <div key={rule.id || idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border group">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <Icon icon="lucide:calendar-range" className="text-primary text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{rule.label}</p>
                <p className="text-xs text-muted-foreground">
                  {rule.startDate} → {rule.endDate} &nbsp;·&nbsp; {currencySymbol}{parseFloat(rule.price).toLocaleString()}/night
                </p>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => openEdit(idx)} className="p-1.5 hover:bg-card rounded-lg text-muted-foreground hover:text-primary transition-colors">
                  <Icon icon="lucide:pencil" className="text-sm" />
                </button>
                <button type="button" onClick={() => handleDelete(idx)} className="p-1.5 hover:bg-card rounded-lg text-muted-foreground hover:text-destructive transition-colors">
                  <Icon icon="lucide:trash-2" className="text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rules.length === 0 && editingIndex === null && (
        <div className="text-center py-6 border-2 border-dashed border-border rounded-xl">
          <Icon icon="lucide:calendar-range" className="text-3xl text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No custom pricing rules yet</p>
          <p className="text-xs text-muted-foreground mt-1">Base price will be used for all dates</p>
        </div>
      )}

      {/* Add/Edit form */}
      {editingIndex !== null && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl space-y-4">
          <p className="text-sm font-bold">{editingIndex === 'new' ? 'Add Pricing Rule' : 'Edit Pricing Rule'}</p>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Rule Label</label>
              <input
                type="text"
                value={draft.label}
                onChange={e => setDraft(d => ({ ...d, label: e.target.value }))}
                placeholder="e.g. Christmas Season, Peak Period"
                className="w-full px-4 py-2.5 bg-card border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Start Date</label>
              <input
                type="date"
                value={draft.startDate}
                onChange={e => setDraft(d => ({ ...d, startDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-card border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">End Date</label>
              <input
                type="date"
                value={draft.endDate}
                onChange={e => setDraft(d => ({ ...d, endDate: e.target.value }))}
                className="w-full px-4 py-2.5 bg-card border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Nightly Rate ({currencySymbol})</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-bold">{currencySymbol}</span>
                <input
                  type="number"
                  value={draft.price}
                  onChange={e => setDraft(d => ({ ...d, price: e.target.value }))}
                  placeholder="0.00"
                  min="0"
                  className="w-full pl-8 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={handleCancel} className="px-4 py-2 border border-border text-muted-foreground rounded-xl text-xs font-bold hover:bg-muted transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity">
              {editingIndex === 'new' ? 'Add Rule' : 'Save Rule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
